import { LuCheck, LuSearch } from 'react-icons/lu';
import { Alert, Input, InputGroup, InputGroupText, Label } from 'reactstrap';

import { formatMoney } from 'src/shared/utils/money';
import { formatDuration } from '../calendarUtils';
import type { AppointmentServiceLine } from '../types';
import type { ServiceSelection } from './useServiceSelection';

/** Past this many services, a search box earns its place above the list. */
const SEARCH_FROM = 8;

interface ServicePickerProps {
  form: ServiceSelection;
  currency?: string;
  /**
   * The lines an appointment already holds, by service. One of these is shown
   * at the price and the length it was booked at, because that is what it keeps
   * if it stays; everything else shows today's catalogue.
   */
  held?: Record<number, Pick<AppointmentServiceLine, 'unitPrice' | 'durationMinutes'>>;
}

/** What is being booked: the catalogue by category, tap to add or remove. */
export default function ServicePicker({ form, currency, held }: ServicePickerProps) {
  if (form.services.isPending) {
    return <div className="text-muted small">Cargando el catálogo…</div>;
  }
  if (form.catalogue.length === 0) {
    return (
      <Alert color="warning" role="status" className="mb-0">
        No tienes servicios activos. Agrégalos en el catálogo antes de agendar.
      </Alert>
    );
  }

  return (
    <div>
      {form.catalogue.length > SEARCH_FROM && (
        <div className="mb-3">
          <Label for="bookServiceSearch" className="visually-hidden">
            Buscar servicio
          </Label>
          <InputGroup>
            <InputGroupText>
              <LuSearch aria-hidden="true" />
            </InputGroupText>
            <Input
              id="bookServiceSearch"
              type="search"
              autoComplete="off"
              placeholder="Buscar servicio"
              value={form.serviceSearch}
              onChange={(e) => form.setServiceSearch(e.target.value)}
            />
          </InputGroup>
        </div>
      )}

      {form.serviceGroups.length === 0 && (
        <p className="text-muted small">Ningún servicio coincide con esa búsqueda.</p>
      )}

      {form.serviceGroups.map((group) => (
        <div key={group.key} className="v-service-group" role="group" aria-label={group.label}>
          <p className="v-service-group__label">
            {group.color && (
              <span className="v-dot" style={{ backgroundColor: group.color }} aria-hidden="true" />
            )}
            {group.label}
          </p>
          {group.services.map((service) => {
            const selected = form.serviceIds.includes(service.id);
            const line = held?.[service.id];
            return (
              <button
                key={service.id}
                type="button"
                className="v-pick-list__row v-service-option"
                aria-pressed={selected}
                onClick={() => form.toggleService(service.id)}
              >
                <span className="v-service-option__check" aria-hidden="true">
                  {selected && <LuCheck />}
                </span>
                <span className="v-pick-list__text">
                  <span className="v-pick-list__name">{service.name}</span>
                  <span className="v-pick-list__hint">
                    {formatDuration(line?.durationMinutes ?? service.durationMinutes)}
                    {!service.isActive && ' · ya no se ofrece'}
                  </span>
                </span>
                <span className="v-money fw-semibold">
                  {formatMoney(line?.unitPrice ?? service.currentPrice, currency)}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
